import { useCached3DObjectIfReady } from "@/object-cache";
import { selectActiveCadModel } from "@/store/cad/cad-slice";
import { selectModeName } from "@/store/mode-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { SceneFilter, usesCadModel } from "@/types/scene-filter";
import {
  convertFromMeter,
  FaroText,
  MeasurementUnits,
  TranslateVar,
  useToast,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import { OutOfCoreStreamCadModel, StreamCadModel } from "@faro-lotv/lotv";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { isMetric } from "./settings-context/distance-unit-utils";
import { useDistanceUnit } from "./settings-context/settings-context";

/**
 * Parameters for useCadModelLoadingProgress hook.
 */
type CadModelLoadingProgressProps = {
  /* Current scene filter value */
  value: SceneFilter;

  /* True if support for CAD is enabled; false is user cannot display CAD models */
  cadEnabled: boolean;
};

type CadModelLoadingProgressReturn = {
  /* True if the CAD model has completed preload and is ready to be displayed */
  isCadModelReady: boolean;

  /* Tip displayed on the Cad Model button showing the progress status (empty when loading is successfully complete) */
  tooltipMessage: ReactNode;

  // Loading progress in % from 0 = "nothing loaded yet" to 100 = whole model has been loaded.
  // Undefined when loading is not in progress.
  cadLoadingProgress: number | undefined;
};

/**
 * Hook to report loading progress of CAD model.
 *
 * @returns See CadModelLoadingProgressReturn
 */
export function useCadModelLoadingProgress({
  value,
  cadEnabled,
}: CadModelLoadingProgressProps): CadModelLoadingProgressReturn {
  const { openToast } = useToast();
  const activeCad = useAppSelector(selectActiveCadModel);
  const cadModelObject = useCached3DObjectIfReady(
    activeCad,
    "CadModelLoadingProgress",
  );

  const isCadModelReady = !(
    cadModelObject instanceof Error || cadModelObject === null
  );

  const isSplitMode = useAppSelector(selectModeName) === "split";

  // CAD model loading progress in %, or undefined when not loading
  const [cadLoadingProgress, setCadLoadingProgress] = useState<
    undefined | number
  >();
  // true when CAD model loading failed due to insufficient memory
  const [cadLoadingFailed, setCadLoadingFailed] = useState(false);

  // Compute setCadLoadingProgress from current loading state
  const setLoadingProgress = useCallback(() => {
    if (cadModelObject instanceof StreamCadModel) {
      const [chunksLoaded, chunksToBeLoaded] = cadModelObject.loadingProgress;
      if (chunksLoaded === chunksToBeLoaded) {
        setCadLoadingProgress(undefined);
      } else {
        setCadLoadingProgress((100 * chunksLoaded) / chunksToBeLoaded);
      }
      setCadLoadingFailed(
        cadModelObject.isCompleted && chunksLoaded !== chunksToBeLoaded,
      );
    } else if (cadModelObject instanceof OutOfCoreStreamCadModel) {
      setCadLoadingFailed(false);
      if (
        cadModelObject.chunksInMemory === cadModelObject.totalChunks ||
        cadModelObject.visibleChunksInFrustum === cadModelObject.chunksInFrustum
      ) {
        setCadLoadingProgress(undefined);
      } else {
        setCadLoadingProgress(
          (100 * cadModelObject.visibleChunksInFrustum) /
            cadModelObject.chunksInFrustum,
        );
      }
    } else {
      setCadLoadingProgress(undefined);
      setCadLoadingFailed(false);
    }
  }, [cadModelObject]);

  // Initialize loading progress when the component is mounted or when the active model changed
  useEffect(() => {
    setLoadingProgress();
  }, [setLoadingProgress]);

  // Event listening for StreamCadModel loading a new chunk of data
  useTypedEvent<void>(
    cadModelObject instanceof StreamCadModel ||
      cadModelObject instanceof OutOfCoreStreamCadModel
      ? cadModelObject.chunkLoaded
      : undefined,
    setLoadingProgress,
  );

  useTypedEvent<void>(
    cadModelObject instanceof OutOfCoreStreamCadModel
      ? cadModelObject.chunkDisposed
      : undefined,
    setLoadingProgress,
  );

  useTypedEvent<void>(
    cadModelObject instanceof OutOfCoreStreamCadModel
      ? cadModelObject.streamingStopped
      : undefined,
    setLoadingProgress,
  );

  // Event listening for StreamCadModel stopping the load
  // (either because the model is complete in memory, or there is not enough space t load it)
  useTypedEvent<void>(
    cadModelObject instanceof StreamCadModel
      ? cadModelObject.streamCompleted
      : undefined,
    () => {
      if (cadModelObject instanceof StreamCadModel) {
        const [chunksLoaded, chunksToBeLoaded] = cadModelObject.loadingProgress;
        setCadLoadingFailed(chunksLoaded !== chunksToBeLoaded);
        if (chunksLoaded !== chunksToBeLoaded) {
          openToast({
            title: (
              <>
                Model render stopped at{" "}
                <TranslateVar name="loadingProgress">
                  {getCadLoadingProgressString(
                    (100 * chunksLoaded) / chunksToBeLoaded,
                  )}
                </TranslateVar>
              </>
            ),
            variant: "warning",
            persist: true,
            message:
              "Try reducing the details of the model to ensure that it can be loaded completely.",
          });
        }
      }
    },
  );

  const distanceUnit = useDistanceUnit();

  // Return the Element display the visibility range
  const computeVisibilityDistance = useCallback(
    (cadModelObject: OutOfCoreStreamCadModel) => {
      if (isMetric(distanceUnit)) {
        return (
          <>
            Visibility up to{" "}
            <TranslateVar name="visibleDistanceInMeter">
              {cadModelObject.maxVisibleChunkCameraDistance.toFixed(1)} m
            </TranslateVar>
          </>
        );
      }
      const feet = convertFromMeter(
        cadModelObject.maxVisibleChunkCameraDistance,
        MeasurementUnits.feet,
      );
      return (
        <>
          Visibility up to{" "}
          <TranslateVar name="visibleDistanceInFeet">
            {feet.toFixed(1)} ft
          </TranslateVar>
        </>
      );
    },
    [distanceUnit],
  );

  // Tooltip message to be displayed on the Cad Model button
  const tooltipMessage = useMemo(() => {
    if (isSplitMode && !cadEnabled && isCadModelReady) {
      return "3D Model is already displayed in the other panel";
    }
    const progressDisplay = getCadLoadingProgressString(cadLoadingProgress);
    if (progressDisplay && usesCadModel(value)) {
      if (cadLoadingFailed) {
        return (
          <FaroText variant="bodyS" color="white">
            Model stopped loading at{" "}
            <TranslateVar name="progressDisplay">
              {progressDisplay}
            </TranslateVar>
          </FaroText>
        );
      }
      if (cadModelObject instanceof OutOfCoreStreamCadModel) {
        if (cadModelObject.isStreaming) {
          return (
            <FaroText variant="bodyS" color="white">
              Loading model (
              <TranslateVar name="progressDisplay">
                {progressDisplay}
              </TranslateVar>
              )
              <br />
              {computeVisibilityDistance(cadModelObject)}
            </FaroText>
          );
        }
        return (
          <FaroText variant="bodyS" color="white">
            Reached memory limit, stopped at{" "}
            <TranslateVar name="progressDisplay">
              {progressDisplay}
            </TranslateVar>
            <br />
            {computeVisibilityDistance(cadModelObject)}
          </FaroText>
        );
      }
      return (
        <FaroText variant="bodyS" color="white">
          Loading model (
          <TranslateVar name="progressDisplay">{progressDisplay}</TranslateVar>)
        </FaroText>
      );
    }
    return "";
  }, [
    isSplitMode,
    cadEnabled,
    isCadModelReady,
    cadLoadingProgress,
    value,
    cadLoadingFailed,
    cadModelObject,
    computeVisibilityDistance,
  ]);

  return { isCadModelReady, tooltipMessage, cadLoadingProgress };
}

/**
 * @returns Loading progress as a string (e.g. "22%"), or undefined if not loading
 * @param cadLoadingProgress loading progress in %, or undefined when not loading
 */
function getCadLoadingProgressString(
  cadLoadingProgress: number | undefined,
): string | undefined {
  return cadLoadingProgress ? `${Math.round(cadLoadingProgress)}%` : undefined;
}
